import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, catchError, switchMap, tap, throwError } from 'rxjs';
import { StateApiError, checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError } from '@trovata/app/core/models/state-error.model';
import { StablecoinKybService } from '../../../services/kyb/kyb.service';
import { KybApplication, KybChangeRequest, StablecoinLegalEntity } from '../../../models/kyb.model';
import {
	ClearStablecoinKybState,
	CreateStablecoinLegalEntity,
	DecideStablecoinKybChangeRequest,
	GetStablecoinKybApplication,
	GetStablecoinKybChangeRequests,
	GetStablecoinLegalEntities,
	InitStablecoinKybState,
} from '../../actions/kyb.actions';

export interface StablecoinKybStateModel {
	legalEntities: StablecoinLegalEntity[] | null;
	legalEntitiesLoading: boolean;
	legalEntitiesLoadFailed: boolean;
	isCached: boolean;
	applicationsByEntityId: { [entityId: string]: KybApplication };
	changeRequestsByEntityId: { [entityId: string]: KybChangeRequest[] };
	apiErrors: StateApiError[];
}

const DEFAULT_STABLECOIN_KYB_STATE: StablecoinKybStateModel = {
	legalEntities: null,
	legalEntitiesLoading: false,
	legalEntitiesLoadFailed: false,
	isCached: false,
	applicationsByEntityId: {},
	changeRequestsByEntityId: {},
	apiErrors: [],
};

/** Legal entities and their Coinbase onboarding applications (TPMK-8167). Never persisted: applications carry masked PII. */
@State<StablecoinKybStateModel>({
	name: 'stablecoinKyb',
	defaults: { ...DEFAULT_STABLECOIN_KYB_STATE },
})
@Injectable()
export class StablecoinKybState {
	private readonly kybService: StablecoinKybService = inject(StablecoinKybService);

	@Selector()
	static legalEntities(state: StablecoinKybStateModel): StablecoinLegalEntity[] | null {
		return state.legalEntities;
	}

	@Selector()
	static legalEntitiesLoading(state: StablecoinKybStateModel): boolean {
		return state.legalEntitiesLoading;
	}

	@Selector()
	static legalEntitiesLoadFailed(state: StablecoinKybStateModel): boolean {
		return state.legalEntitiesLoadFailed;
	}

	@Selector()
	static applicationsByEntityId(state: StablecoinKybStateModel): { [entityId: string]: KybApplication } {
		return state.applicationsByEntityId;
	}

	@Selector()
	static changeRequestsByEntityId(state: StablecoinKybStateModel): { [entityId: string]: KybChangeRequest[] } {
		return state.changeRequestsByEntityId;
	}

	@Action(InitStablecoinKybState)
	init(): void {
		// Fetched lazily from the pages that need it.
	}

	@Action(ClearStablecoinKybState)
	clear(context: StateContext<StablecoinKybStateModel>): void {
		context.setState({ ...DEFAULT_STABLECOIN_KYB_STATE });
	}

	@Action(GetStablecoinLegalEntities)
	getLegalEntities(context: StateContext<StablecoinKybStateModel>, action: GetStablecoinLegalEntities): Observable<StablecoinLegalEntity[]> | void {
		if (context.getState().isCached && !action.refresh) {
			return;
		}
		context.patchState({ legalEntitiesLoading: true, legalEntitiesLoadFailed: false });
		return this.kybService.getLegalEntities().pipe(
			tap((legalEntities: StablecoinLegalEntity[]) => {
				checkToRemoveStateApiError(context, GetStablecoinLegalEntities);
				context.patchState({ legalEntities, isCached: true, legalEntitiesLoading: false });
			}),
			catchError((error: HttpErrorResponse) => {
				context.patchState({ legalEntitiesLoading: false, legalEntitiesLoadFailed: true });
				checkToAddStateApiError(error, context, GetStablecoinLegalEntities, checkForBadHttpErrorMessage(error, 'Unable to load legal entities.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(CreateStablecoinLegalEntity)
	createLegalEntity(context: StateContext<StablecoinKybStateModel>, action: CreateStablecoinLegalEntity): Observable<StablecoinLegalEntity> {
		return this.kybService.createLegalEntity(action.body).pipe(
			tap((entity: StablecoinLegalEntity) => {
				checkToRemoveStateApiError(context, CreateStablecoinLegalEntity);
				const existing: StablecoinLegalEntity[] = context.getState().legalEntities ?? [];
				context.patchState({ legalEntities: [...existing.filter((item: StablecoinLegalEntity) => item.id !== entity.id), entity] });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, CreateStablecoinLegalEntity, checkForBadHttpErrorMessage(error, 'Unable to create the legal entity.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinKybApplication)
	getApplication(context: StateContext<StablecoinKybStateModel>, action: GetStablecoinKybApplication): Observable<KybApplication> {
		return this.kybService.getApplication(action.entityId).pipe(
			tap((application: KybApplication) => {
				checkToRemoveStateApiError(context, GetStablecoinKybApplication);
				context.patchState({ applicationsByEntityId: { ...context.getState().applicationsByEntityId, [action.entityId]: application } });
				this.syncEntityFromApplication(context, action.entityId, application);
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetStablecoinKybApplication, checkForBadHttpErrorMessage(error, 'Unable to load onboarding.').message);
				return throwError(() => error);
			})
		);
	}

	@Action(GetStablecoinKybChangeRequests)
	getChangeRequests(context: StateContext<StablecoinKybStateModel>, action: GetStablecoinKybChangeRequests): Observable<KybChangeRequest[]> {
		return this.kybService.getChangeRequests(action.entityId).pipe(
			tap((changeRequests: KybChangeRequest[]) => {
				checkToRemoveStateApiError(context, GetStablecoinKybChangeRequests);
				context.patchState({ changeRequestsByEntityId: { ...context.getState().changeRequestsByEntityId, [action.entityId]: changeRequests } });
			}),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, GetStablecoinKybChangeRequests, checkForBadHttpErrorMessage(error, 'Unable to load change requests.').message);
				return throwError(() => error);
			})
		);
	}

	/** Decide, then refetch the list (the decide response shape isn't in the contract). */
	@Action(DecideStablecoinKybChangeRequest)
	decideChangeRequest(context: StateContext<StablecoinKybStateModel>, action: DecideStablecoinKybChangeRequest): Observable<void> {
		return this.kybService.decideChangeRequest(action.entityId, action.changeRequestId, action.decision).pipe(
			switchMap(() => context.dispatch(new GetStablecoinKybChangeRequests(action.entityId))),
			catchError((error: HttpErrorResponse) => {
				checkToAddStateApiError(error, context, DecideStablecoinKybChangeRequest, checkForBadHttpErrorMessage(error, 'Unable to record the decision.').message);
				return throwError(() => error);
			})
		);
	}

	/** Keeps the cached list's status/capability in step with a freshly fetched application. */
	private syncEntityFromApplication(context: StateContext<StablecoinKybStateModel>, entityId: string, application: KybApplication): void {
		const entities: StablecoinLegalEntity[] | null = context.getState().legalEntities;
		if (!entities) {
			return;
		}
		context.patchState({
			legalEntities: entities.map((entity: StablecoinLegalEntity) =>
				entity.id === entityId ? { ...entity, kyb_status: application.status, capability: application.tracker?.capability ?? entity.capability } : entity
			),
		});
	}
}
